import { inject, Injectable } from '@angular/core';
import {
	FirebaseStorage,
	getDownloadURL,
	ref,
	uploadBytesResumable,
} from '@marmon/frontend/firebase/storage';
import { createAnalysisCsvStoragePath } from '@marmon/shared/types/common';

export type FileProgress = {
	totalBytes: number;
	bytesTransferred: number;
	complete: boolean;
	percent: number;
};

@Injectable({ providedIn: 'root' })
export class StorageService {
	private readonly storage = inject(FirebaseStorage);

	/**
	 * Uploads the CSV to a unique Storage object under the analysis prefix.
	 * Returns the object path used for the upload so import can target it
	 * without reconstructing a file name.
	 */
	uploadCsv(
		file: File,
		options: { id: string; userId: string },
		callback: (progress: FileProgress) => void,
	): string {
		const { id, userId } = options;
		const { storagePath } = createAnalysisCsvStoragePath(userId, id);
		const storageRef = ref(this.storage, storagePath);

		const task = uploadBytesResumable(storageRef, file);
		task.on('state_changed', ({ bytesTransferred, totalBytes }) => {
			const rounded =
				bytesTransferred !== 0
					? Math.round((bytesTransferred / totalBytes) * 100)
					: 0;
			const percent = Math.min(rounded, 100);
			callback({
				percent,
				totalBytes,
				bytesTransferred,
				complete: percent === 100,
			});
		});

		return storagePath;
	}

	async getObjectBlob(storagePath: string): Promise<Blob> {
		const url = await getDownloadURL(ref(this.storage, storagePath));
		const response = await fetch(url);
		if (!response.ok) {
			throw new Error(`Failed to download storage object ${storagePath}`);
		}
		return response.blob();
	}
}
